import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Plus, Pencil, Trash2, Square } from 'lucide-react';
import { PageHeader } from '../common/ui';
import { useAuth } from '../../contexts/AuthContext';
import { Project, TimeEntry, ActiveTimer } from '../../types/time';
import TimeNav from './TimeNav';

function formatDuration(seconds: number | null): string {
  if (seconds === null || seconds === undefined) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function formatElapsed(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
}

function groupByDate(entries: TimeEntry[]): Record<string, TimeEntry[]> {
  const groups: Record<string, TimeEntry[]> = {};
  for (const entry of entries) {
    const dateKey = new Date(entry.start_time).toISOString().split('T')[0];
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(entry);
  }
  return groups;
}

const TimeTracker: React.FC = () => {
  const { user } = useAuth() as any;
  const [projects, setProjects] = useState<Project[]>([])
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [showManual, setShowManual] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [filterUser, setFilterUser] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStart, setFilterStart] = useState('');
  const [filterEnd, setFilterEnd] = useState('');
  const [users, setUsers] = useState<{ id: number; name: string; is_active?: boolean }[]>([]);
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const [activeDescription, setActiveDescription] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [manualForm, setManualForm] = useState({
    project_id: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    start_time: '09:00',
    end_time: '17:00'
  });

  useEffect(() => {
    if (user?.time_master) {
      axios.get('/api/users').then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data.users || [];
        setUsers(data);
      }).catch(() => {});
    }
  }, [user?.time_master]);

  const fetchActiveTimer = useCallback(async () => {
    try {
      const res = await axios.get('/api/time-entries/active');
      setActiveTimer(res.data);
      setActiveDescription(res.data?.description || '');
    } catch {
      setActiveTimer(null);
    }
  }, []);

  useEffect(() => {
    fetchActiveTimer();
    const onChanged = () => { fetchActiveTimer(); fetchData(); };
    window.addEventListener('time-entries-changed', onChanged);
    return () => window.removeEventListener('time-entries-changed', onChanged);
  }, [fetchActiveTimer]);

  useEffect(() => {
    if (activeTimer) {
      const startTime = new Date(activeTimer.start_time).getTime();
      const tick = () => setElapsed(Math.max(0, Math.floor((Date.now() - startTime) / 1000)));
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed(0);
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [activeTimer]);

  const handleStopTimer = async () => {
    if (!activeTimer || activeTimer.id < 0) return;
    try {
      await axios.patch(`/api/time-entries/${activeTimer.id}/stop`);
      setActiveTimer(null);
      fetchData();
      window.dispatchEvent(new Event('time-entries-changed'));
    } catch (err) {
      console.error('Error stopping timer:', err);
    }
  };

  const saveActiveDescription = async (desc: string) => {
    if (!activeTimer || activeTimer.id < 0) return;
    setSavingDescription(true);
    try {
      await axios.put(`/api/time-entries/${activeTimer.id}`, {
        description: desc || null
      });
      setActiveTimer(prev => prev ? { ...prev, description: desc || null } : prev);
    } catch (err) {
      console.error('Error saving description:', err);
    } finally {
      setSavingDescription(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('limit', '200');
      if (filterUser) params.set('user_id', filterUser);
      if (filterProject) params.set('project_id', filterProject);
      if (filterStart) params.set('start', filterStart);
      if (filterEnd) params.set('end', filterEnd);

      const [projectsRes, entriesRes] = await Promise.all([
        axios.get('/api/projects?status=active'),
        axios.get(`/api/time-entries?${params.toString()}`)
      ]);
      setProjects(projectsRes.data);
      setEntries(entriesRes.data.entries || []);
    } catch (err) {
      console.error('Error fetching time data:', err);
    } finally {
      setLoading(false);
    }
  }, [filterUser, filterProject, filterStart, filterEnd]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualForm.project_id) return;

    const startDate = new Date(`${manualForm.date}T${manualForm.start_time}:00`);
    const endDate = new Date(`${manualForm.date}T${manualForm.end_time}:00`);
    const startDateTime = startDate.toISOString();
    const endDateTime = endDate.toISOString();

    try {
      if (editingEntry) {
        await axios.put(`/api/time-entries/${editingEntry.id}`, {
          project_id: parseInt(manualForm.project_id),
          description: manualForm.description || null,
          start_time: startDateTime,
          end_time: endDateTime
        });
      } else {
        await axios.post('/api/time-entries', {
          project_id: parseInt(manualForm.project_id),
          description: manualForm.description || null,
          start_time: startDateTime,
          end_time: endDateTime
        });
      }
      setShowManual(false);
      setEditingEntry(null);
      resetManualForm();
      fetchData();
      window.dispatchEvent(new Event('time-entries-changed'));
    } catch (err: any) {
      console.error('Error saving time entry:', err);
      alert(err.response?.data?.error || 'Failed to save time entry');
    }
  };

  const resetManualForm = () => {
    setManualForm({
      project_id: '',
      description: '',
      date: new Date().toISOString().split('T')[0],
      start_time: '09:00',
      end_time: '17:00'
    });
  };

  const handleEdit = (entry: TimeEntry) => {
    setEditingEntry(entry);
    const startDate = new Date(entry.start_time);
    const endDate = entry.end_time ? new Date(entry.end_time) : new Date();
    setManualForm({
      project_id: entry.project_id.toString(),
      description: entry.description || '',
      date: startDate.toISOString().split('T')[0],
      start_time: startDate.toTimeString().slice(0, 5),
      end_time: endDate.toTimeString().slice(0, 5)
    });
    setShowManual(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this time entry?')) return;
    try {
      await axios.delete(`/api/time-entries/${id}`);
      fetchData();
      window.dispatchEvent(new Event('time-entries-changed'));
    } catch (err) {
      console.error('Error deleting entry:', err);
    }
  };

  const grouped = groupByDate(entries.filter(e => e.end_time !== null && e.id !== activeTimer?.id));

  return (
    <div className="p-6">
      <TimeNav />

      <div className="flex justify-between items-center mb-6">
        <PageHeader title="Time Tracker" subtitle="" />
        <button
          onClick={() => {
            setEditingEntry(null);
            resetManualForm();
            setShowManual(true);
          }}
          className="bg-naviter-blue hover:bg-naviter-blue-dark text-white px-4 py-2 rounded-lg flex items-center gap-2 text-sm"
        >
          <Plus size={16} />
          Manual Entry
        </button>
      </div>

      {/* Active timer card */}
      {activeTimer && activeTimer.id > 0 && (
        <div
          className="bg-white rounded-lg shadow mb-6 border-l-4 overflow-hidden"
          style={{ borderLeftColor: activeTimer.project_color }}
        >
          <div className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className="relative flex h-2.5 w-2.5">
                  <span
                    className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-75"
                    style={{ backgroundColor: activeTimer.project_color }}
                  />
                  <span
                    className="relative inline-flex rounded-full h-2.5 w-2.5"
                    style={{ backgroundColor: activeTimer.project_color }}
                  />
                </span>
                <span className="text-sm font-semibold text-gray-900">{activeTimer.project_name}</span>
              </div>
              <input
                type="text"
                value={activeDescription}
                onChange={(e) => setActiveDescription(e.target.value)}
                onBlur={() => saveActiveDescription(activeDescription)}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                placeholder="What are you working on?"
                className={`flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:ring-naviter-blue focus:border-naviter-blue ${savingDescription ? 'opacity-60' : ''}`}
              />
              <span className="text-lg font-mono font-semibold text-gray-900 tabular-nums flex-shrink-0">
                {formatElapsed(elapsed)}
              </span>
              <button
                onClick={handleStopTimer}
                className="p-2 rounded-lg text-white hover:opacity-90 transition-opacity flex-shrink-0"
                style={{ backgroundColor: activeTimer.project_color }}
                title="Stop timer"
              >
                <Square size={16} fill="currentColor" />
              </button>
            </div>
            <div className="text-xs text-gray-400 mt-1.5 ml-[18px]">
              Started at {formatTime(activeTimer.start_time)}
            </div>
          </div>
        </div>
      )}

      {/* No projects prompt */}
      {!loading && projects.length === 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="text-amber-800 text-sm">
            No projects have been created yet.{' '}
            {user?.time_master ? (
              <Link to="/time/projects" className="font-medium underline hover:text-amber-900">
                Create your first project
              </Link>
            ) : (
              <span>Ask a time master to create projects.</span>
            )}
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          {user?.time_master && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <select
                value={filterUser}
                onChange={(e) => setFilterUser(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
              >
                <option value="">All employees</option>
                {users.map(u => (
                  <option key={u.id} value={u.id}>{u.name}{u.is_active === false ? ' (inactive)' : ''}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
            <select
              value={filterProject}
              onChange={(e) => setFilterProject(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
            >
              <option value="">All projects</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="date"
              value={filterStart}
              onChange={(e) => setFilterStart(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="date"
              value={filterEnd}
              onChange={(e) => setFilterEnd(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
            />
          </div>
          {(filterUser || filterProject || filterStart || filterEnd) && (
            <button
              onClick={() => { setFilterUser(''); setFilterProject(''); setFilterStart(''); setFilterEnd(''); }}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Entries grouped by day */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Clock size={48} className="mx-auto mb-4 text-gray-300" />
          <p>No time entries found.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, dayEntries]) => {
            const dayTotal = dayEntries.reduce((sum, e) => sum + (e.duration_seconds || 0), 0);
            return (
              <div key={date} className="bg-white rounded-lg shadow">
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100">
                  <span className="text-sm font-semibold text-gray-900">{formatDate(date)}</span>
                  <span className="text-sm font-medium text-gray-500">{formatDuration(dayTotal)}</span>
                </div>
                <div className="divide-y divide-gray-50">
                  {dayEntries.map(entry => (
                    <div key={entry.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50">
                      <span
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: entry.project_color }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">{entry.project_name}</span>
                          {entry.description && (
                            <span className="text-sm text-gray-500 truncate">— {entry.description}</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-400">
                          {formatTime(entry.start_time)} – {entry.end_time ? formatTime(entry.end_time) : 'running'}
                          {entry.user_name && <span className="ml-2">by {entry.user_name}</span>}
                        </div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 tabular-nums">
                        {formatDuration(entry.duration_seconds)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-1 text-gray-400 hover:text-blue-600 rounded"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Manual entry / Edit modal */}
      {showManual && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 lg:w-1/3 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingEntry ? 'Edit Time Entry' : 'Manual Time Entry'}
            </h3>
            <form onSubmit={handleManualSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Project *</label>
                <select
                  value={manualForm.project_id}
                  onChange={(e) => setManualForm(prev => ({ ...prev, project_id: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                >
                  <option value="">Select project...</option>
                  {projects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={manualForm.description}
                  onChange={(e) => setManualForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What did you work on?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
                <input
                  type="date"
                  value={manualForm.date}
                  onChange={(e) => setManualForm(prev => ({ ...prev, date: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start time *</label>
                  <input
                    type="time"
                    value={manualForm.start_time}
                    onChange={(e) => setManualForm(prev => ({ ...prev, start_time: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End time *</label>
                  <input
                    type="time"
                    value={manualForm.end_time}
                    onChange={(e) => setManualForm(prev => ({ ...prev, end_time: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-naviter-blue focus:border-naviter-blue"
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => { setShowManual(false); setEditingEntry(null); }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-naviter-blue text-white rounded-md hover:bg-naviter-blue-dark text-sm"
                >
                  {editingEntry ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTracker;
